<script setup lang="ts">
import { ref, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import QRCode from 'qrcode';
import { useAuth } from '../composables/useAuth';
import {
  authAPI,
  type UpdateProfileRequest,
  DEFAULT_USER_SEX,
  DEFAULT_REMINDER_HOURS,
  MAX_REMINDER_HOURS,
  DEFAULT_MAX_VISITS_PER_DAY,
  MAX_VISITS_PER_DAY,
  DEFAULT_SLOT_STEP_MINUTES,
  SLOT_STEP_OPTIONS,
  SEX_OPTIONS,
  type UserSex,
} from '../services/api';
import WeeklyScheduleCard from '../components/WeeklyScheduleCard.vue';

const { t } = useI18n();
const { user, fetchUser } = useAuth();

const formData = ref({
  first_name: '',
  last_name: '',
  personality: '',
  tone_style: '',
  sex: DEFAULT_USER_SEX as UserSex,
  offensive_limit: '' as number | '',
  reminder_hours: '' as number | '',
  max_visits_per_day: '' as number | '',
});

const formErrors = ref<Record<string, string>>({});

/** One card per tab; the key doubles as the i18n suffix for its label. */
const TABS = ['profile', 'preferences', 'agent', 'schedule', 'password', 'twofa'] as const;
type SettingsTab = (typeof TABS)[number];

const activeTab = ref<SettingsTab>('profile');

/**
 * Three cards edit the same user through PATCH /me, so each needs its own busy flag
 * and its own messages - a save in one must not flash a result in another. Keyed
 * rather than kept as nine refs so a fourth card is one line, not three.
 */
type ProfileSection = 'profile' | 'preferences' | 'agent';
const newFeedback = () => ({ saving: false, success: '', error: '' });
const feedback = ref<Record<ProfileSection, ReturnType<typeof newFeedback>>>({
  profile: newFeedback(),
  preferences: newFeedback(),
  agent: newFeedback(),
});

// Saved through PUT /me/schedule rather than PATCH /me: it belongs to the availability
// engine, not the profile. The hours it pairs with live on their own card.
const slotStepMinutes = ref<number>(DEFAULT_SLOT_STEP_MINUTES);

const passwordData = ref({
  current_password: '',
  new_password: '',
  confirm_new_password: '',
});

const passwordErrors = ref<Record<string, string>>({});
const isSavingPassword = ref(false);
const passwordSuccessMessage = ref('');
const passwordErrorMessage = ref('');

onMounted(async () => {
  if (!user.value) {
    await fetchUser();
  }
  if (user.value) {
    formData.value.first_name = user.value.first_name;
    formData.value.last_name = user.value.last_name;
    formData.value.personality = user.value.personality ?? '';
    formData.value.tone_style = user.value.tone_style ?? '';
    formData.value.sex = user.value.sex ?? DEFAULT_USER_SEX;
    formData.value.offensive_limit = user.value.offensive_limit ?? '';
    formData.value.reminder_hours = user.value.reminder_hours ?? '';
    formData.value.max_visits_per_day = user.value.max_visits_per_day ?? '';
    slotStepMinutes.value = user.value.slot_step_minutes ?? DEFAULT_SLOT_STEP_MINUTES;
  }
});

// Vue applies the .number modifier automatically to type="number", so these fields
// hold a number once something is typed and '' while empty. Empty means "use the
// deployment default", which the API takes as null.
const optionalNumber = (raw: number | ''): number | null => (raw === '' ? null : raw);

/** Only the fields the card being saved actually shows - the rest is not its business. */
const validateSection = (section: ProfileSection): boolean => {
  formErrors.value = {};

  if (section === 'profile') {
    if (!formData.value.first_name.trim()) {
      formErrors.value.first_name = t('settings.error_first_name_required');
    }
    if (!formData.value.last_name.trim()) {
      formErrors.value.last_name = t('settings.error_last_name_required');
    }

    return Object.keys(formErrors.value).length === 0;
  }

  if (section !== 'preferences') {
    return true;
  }

  const limit = optionalNumber(formData.value.offensive_limit);
  if (limit !== null && (!Number.isInteger(limit) || limit < 1)) {
    formErrors.value.offensive_limit = t('settings.error_offensive_limit');
  }

  const hours = optionalNumber(formData.value.reminder_hours);
  if (hours !== null && (!Number.isInteger(hours) || hours < 1 || hours > MAX_REMINDER_HOURS)) {
    formErrors.value.reminder_hours = t('settings.error_reminder_hours', {
      max: MAX_REMINDER_HOURS,
    });
  }

  const maxVisits = optionalNumber(formData.value.max_visits_per_day);
  if (
    maxVisits !== null &&
    (!Number.isInteger(maxVisits) || maxVisits < 1 || maxVisits > MAX_VISITS_PER_DAY)
  ) {
    formErrors.value.max_visits_per_day = t('settings.error_max_visits_per_day', {
      max: MAX_VISITS_PER_DAY,
    });
  }

  return Object.keys(formErrors.value).length === 0;
};

// PATCH /me leaves out what it is not sent, so a card carries its own fields and
// nothing else; nobody has to resend a value they are not editing to keep it.
const sectionPayload: Record<ProfileSection, () => UpdateProfileRequest> = {
  profile: () => ({
    first_name: formData.value.first_name.trim(),
    last_name: formData.value.last_name.trim(),
  }),
  agent: () => ({
    sex: formData.value.sex,
    personality: formData.value.personality,
    tone_style: formData.value.tone_style,
  }),
  preferences: () => ({
    offensive_limit: optionalNumber(formData.value.offensive_limit),
    reminder_hours: optionalNumber(formData.value.reminder_hours),
    max_visits_per_day: optionalNumber(formData.value.max_visits_per_day),
  }),
};

const handleSubmit = async (section: ProfileSection = 'profile') => {
  Object.values(feedback.value).forEach((state) => {
    state.success = '';
    state.error = '';
  });

  if (!validateSection(section)) return;

  const state = feedback.value[section];
  state.saving = true;
  try {
    await authAPI.updateProfile(sectionPayload[section]());

    // The slot step sits on the same card but on a different endpoint, so saving that
    // card saves both. The week itself is left out and stays as it was.
    if (section === 'preferences') {
      await authAPI.updateWeeklySchedule({ slot_step_minutes: slotStepMinutes.value });
    }

    await fetchUser();
    state.success = t('settings.save_success');
  } catch {
    state.error = t('settings.save_error');
  } finally {
    state.saving = false;
  }
};

const validatePasswordForm = (): boolean => {
  passwordErrors.value = {};

  if (!passwordData.value.current_password) {
    passwordErrors.value.current_password = t('settings.error_current_password_required');
  }
  if (!passwordData.value.new_password) {
    passwordErrors.value.new_password = t('settings.error_new_password_required');
  }
  if (!passwordData.value.confirm_new_password) {
    passwordErrors.value.confirm_new_password = t('settings.error_confirm_password_required');
  }
  if (
    passwordData.value.new_password &&
    passwordData.value.confirm_new_password &&
    passwordData.value.new_password !== passwordData.value.confirm_new_password
  ) {
    passwordErrors.value.confirm_new_password = t('settings.error_passwords_mismatch');
  }

  return Object.keys(passwordErrors.value).length === 0;
};

// 2FA state
type TwoFaMode = 'idle' | 'enabling' | 'disabling';
const twoFaMode = ref<TwoFaMode>('idle');
const twoFaQrDataUrl = ref('');
const twoFaDigits = ref(['', '', '', '', '', '']);
const twoFaInputs = ref<HTMLInputElement[]>([]);
const twoFaCodeError = ref('');
const twoFaSuccessMessage = ref('');
const twoFaErrorMessage = ref('');
const isTwoFaProcessing = ref(false);

const resetTwoFaDigits = () => {
  twoFaDigits.value = ['', '', '', '', '', ''];
};

const twoFaFullCode = () => twoFaDigits.value.join('');

const handleTwoFaDigit = (index: number, event: Event) => {
  const input = event.target as HTMLInputElement;
  const value = input.value.replace(/\D/g, '').slice(-1);
  twoFaDigits.value[index] = value;
  if (value && index < 5) {
    twoFaInputs.value[index + 1]?.focus();
  }
};

const handleTwoFaKeydown = (index: number, event: KeyboardEvent) => {
  if (event.key === 'Backspace' && !twoFaDigits.value[index] && index > 0) {
    twoFaInputs.value[index - 1]?.focus();
  }
};

const handleTwoFaPaste = async (event: ClipboardEvent) => {
  event.preventDefault();
  const pasted = event.clipboardData?.getData('text') ?? '';
  const digits = pasted.replace(/\D/g, '').slice(0, 6).split('');
  digits.forEach((d, i) => {
    if (i < 6) twoFaDigits.value[i] = d;
  });
  await nextTick();
  const nextEmpty = twoFaDigits.value.findIndex((d) => d === '');
  const focusIndex = nextEmpty === -1 ? 5 : nextEmpty;
  twoFaInputs.value[focusIndex]?.focus();
};

const startEnable2fa = async () => {
  twoFaMode.value = 'enabling';
  twoFaQrDataUrl.value = '';
  resetTwoFaDigits();
  twoFaCodeError.value = '';
  twoFaSuccessMessage.value = '';
  twoFaErrorMessage.value = '';
  try {
    const { otpauth_url } = await authAPI.get2faSecret();
    twoFaQrDataUrl.value = await QRCode.toDataURL(otpauth_url, { width: 200 });
    await nextTick();
    twoFaInputs.value[0]?.focus();
  } catch {
    twoFaErrorMessage.value = t('settings.twofa_load_error');
    twoFaMode.value = 'idle';
  }
};

const startDisable2fa = async () => {
  twoFaMode.value = 'disabling';
  resetTwoFaDigits();
  twoFaCodeError.value = '';
  twoFaSuccessMessage.value = '';
  twoFaErrorMessage.value = '';
  await nextTick();
  twoFaInputs.value[0]?.focus();
};

const cancelTwoFa = () => {
  twoFaMode.value = 'idle';
  resetTwoFaDigits();
  twoFaCodeError.value = '';
};

const confirmEnable2fa = async () => {
  twoFaCodeError.value = '';
  const code = twoFaFullCode();
  if (code.length < 6) {
    twoFaCodeError.value = t('settings.twofa_enable_error');
    return;
  }
  isTwoFaProcessing.value = true;
  try {
    await authAPI.enable2fa(code);
    await fetchUser();
    twoFaMode.value = 'idle';
    resetTwoFaDigits();
    twoFaSuccessMessage.value = t('settings.twofa_enable_success');
  } catch {
    twoFaCodeError.value = t('settings.twofa_enable_error');
    resetTwoFaDigits();
    await nextTick();
    twoFaInputs.value[0]?.focus();
  } finally {
    isTwoFaProcessing.value = false;
  }
};

const confirmDisable2fa = async () => {
  twoFaCodeError.value = '';
  const code = twoFaFullCode();
  if (code.length < 6) {
    twoFaCodeError.value = t('settings.twofa_disable_error');
    return;
  }
  isTwoFaProcessing.value = true;
  try {
    await authAPI.disable2fa(code);
    await fetchUser();
    twoFaMode.value = 'idle';
    resetTwoFaDigits();
    twoFaSuccessMessage.value = t('settings.twofa_disable_success');
  } catch {
    twoFaCodeError.value = t('settings.twofa_disable_error');
    resetTwoFaDigits();
    await nextTick();
    twoFaInputs.value[0]?.focus();
  } finally {
    isTwoFaProcessing.value = false;
  }
};

const handlePasswordSubmit = async () => {
  passwordSuccessMessage.value = '';
  passwordErrorMessage.value = '';

  if (!validatePasswordForm()) return;

  isSavingPassword.value = true;
  try {
    await authAPI.changePassword({
      current_password: passwordData.value.current_password,
      new_password: passwordData.value.new_password,
      confirm_new_password: passwordData.value.confirm_new_password,
    });
    passwordData.value = { current_password: '', new_password: '', confirm_new_password: '' };
    passwordSuccessMessage.value = t('settings.password_save_success');
  } catch (error: unknown) {
    const axiosError = error as { response?: { data?: { error?: string } } };
    const backendError = axiosError?.response?.data?.error;

    if (backendError === 'Nieprawidłowe aktualne hasło') {
      passwordErrors.value.current_password = t('settings.error_current_password_invalid');
    } else {
      passwordErrorMessage.value = t('settings.password_save_error');
    }
  } finally {
    isSavingPassword.value = false;
  }
};
</script>

<template>
  <div class="min-h-screen bg-[#0A0A0B]">
    <div class="max-w-5xl mx-auto px-4 pt-10 pb-24">
      <h2 class="text-2xl font-semibold text-[#F5F0EA] mb-6">
        {{ $t('settings.title') }}
      </h2>

      <nav class="flex flex-wrap gap-1 bg-[#111113] border border-[#2A2018] rounded-2xl p-1 mb-6">
        <button
          v-for="tab in TABS"
          :key="tab"
          type="button"
          @click="activeTab = tab"
          :class="[
            'px-4 py-2 text-sm rounded-xl transition',
            activeTab === tab
              ? 'bg-[#D4956A] text-[#0A0A0B]'
              : 'text-[#9E9080] hover:text-[#F5F0EA] hover:bg-[#1E1E23]',
          ]"
        >
          {{ $t(`settings.tab_${tab}`) }}
        </button>
      </nav>

      <div v-show="activeTab === 'profile'">
        <div class="bg-[#111113] rounded-2xl border border-[#2A2018] p-6 w-full">
          <h3 class="text-base font-semibold text-[#F5F0EA] mb-6">
            {{ $t('settings.profile_section') }}
          </h3>
          <form @submit.prevent="handleSubmit('profile')" class="space-y-6">
            <!-- First name -->
            <div>
              <label class="block text-sm font-medium text-[#9E9080] mb-1">
                {{ $t('settings.first_name_label') }}
              </label>
              <input
                v-model="formData.first_name"
                type="text"
                class="w-full px-4 py-2 border rounded-xl text-sm bg-[#1E1E23] text-[#F5F0EA] placeholder:text-[#9E9080] focus:outline-none focus:ring-2 focus:ring-[#D4956A]"
                :class="formErrors.first_name ? 'border-red-400' : 'border-[#2A2018]'"
                :placeholder="$t('settings.first_name_placeholder')"
              />
              <p v-if="formErrors.first_name" class="mt-1 text-xs text-red-500">
                {{ formErrors.first_name }}
              </p>
            </div>

            <!-- Last name -->
            <div>
              <label class="block text-sm font-medium text-[#9E9080] mb-1">
                {{ $t('settings.last_name_label') }}
              </label>
              <input
                v-model="formData.last_name"
                type="text"
                class="w-full px-4 py-2 border rounded-xl text-sm bg-[#1E1E23] text-[#F5F0EA] placeholder:text-[#9E9080] focus:outline-none focus:ring-2 focus:ring-[#D4956A]"
                :class="formErrors.last_name ? 'border-red-400' : 'border-[#2A2018]'"
                :placeholder="$t('settings.last_name_placeholder')"
              />
              <p v-if="formErrors.last_name" class="mt-1 text-xs text-red-500">
                {{ formErrors.last_name }}
              </p>
            </div>

            <!-- Messages -->
            <div
              v-if="feedback.profile.success"
              class="px-4 py-3 bg-green-900/20 border border-green-800 rounded-xl text-sm text-green-400"
            >
              {{ feedback.profile.success }}
            </div>
            <div
              v-if="feedback.profile.error"
              class="px-4 py-3 bg-red-900/20 border border-red-800 rounded-xl text-sm text-red-400"
            >
              {{ feedback.profile.error }}
            </div>

            <!-- Submit -->
            <div class="flex justify-end">
              <button
                type="submit"
                :disabled="feedback.profile.saving"
                class="px-6 py-2 bg-[#D4956A] hover:bg-[#E8B48A] text-[#0A0A0B] text-sm rounded-2xl transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="feedback.profile.saving">{{ $t('settings.saving') }}</span>
                <span v-else>{{ $t('settings.save_button') }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <div v-show="activeTab === 'password'">
        <div class="bg-[#111113] rounded-2xl border border-[#2A2018] p-6 w-full">
          <h3 class="text-base font-semibold text-[#F5F0EA] mb-6">
            {{ $t('settings.password_section') }}
          </h3>

          <form @submit.prevent="handlePasswordSubmit" class="space-y-6">
            <!-- Current password -->
            <div>
              <label class="block text-sm font-medium text-[#9E9080] mb-1">
                {{ $t('settings.current_password_label') }}
              </label>
              <input
                v-model="passwordData.current_password"
                type="password"
                class="w-full px-4 py-2 border rounded-xl text-sm bg-[#1E1E23] text-[#F5F0EA] placeholder:text-[#9E9080] focus:outline-none focus:ring-2 focus:ring-[#D4956A]"
                :class="passwordErrors.current_password ? 'border-red-400' : 'border-[#2A2018]'"
                :placeholder="$t('settings.current_password_placeholder')"
                autocomplete="current-password"
              />
              <p v-if="passwordErrors.current_password" class="mt-1 text-xs text-red-500">
                {{ passwordErrors.current_password }}
              </p>
            </div>

            <!-- New password -->
            <div>
              <label class="block text-sm font-medium text-[#9E9080] mb-1">
                {{ $t('settings.new_password_label') }}
              </label>
              <input
                v-model="passwordData.new_password"
                type="password"
                class="w-full px-4 py-2 border rounded-xl text-sm bg-[#1E1E23] text-[#F5F0EA] placeholder:text-[#9E9080] focus:outline-none focus:ring-2 focus:ring-[#D4956A]"
                :class="passwordErrors.new_password ? 'border-red-400' : 'border-[#2A2018]'"
                :placeholder="$t('settings.new_password_placeholder')"
                autocomplete="new-password"
              />
              <p v-if="passwordErrors.new_password" class="mt-1 text-xs text-red-500">
                {{ passwordErrors.new_password }}
              </p>
            </div>

            <!-- Confirm new password -->
            <div>
              <label class="block text-sm font-medium text-[#9E9080] mb-1">
                {{ $t('settings.confirm_password_label') }}
              </label>
              <input
                v-model="passwordData.confirm_new_password"
                type="password"
                class="w-full px-4 py-2 border rounded-xl text-sm bg-[#1E1E23] text-[#F5F0EA] placeholder:text-[#9E9080] focus:outline-none focus:ring-2 focus:ring-[#D4956A]"
                :class="passwordErrors.confirm_new_password ? 'border-red-400' : 'border-[#2A2018]'"
                :placeholder="$t('settings.confirm_password_placeholder')"
                autocomplete="new-password"
              />
              <p v-if="passwordErrors.confirm_new_password" class="mt-1 text-xs text-red-500">
                {{ passwordErrors.confirm_new_password }}
              </p>
            </div>

            <!-- Messages -->
            <div
              v-if="passwordSuccessMessage"
              class="px-4 py-3 bg-green-900/20 border border-green-800 rounded-xl text-sm text-green-400"
            >
              {{ passwordSuccessMessage }}
            </div>
            <div
              v-if="passwordErrorMessage"
              class="px-4 py-3 bg-red-900/20 border border-red-800 rounded-xl text-sm text-red-400"
            >
              {{ passwordErrorMessage }}
            </div>

            <!-- Submit -->
            <div class="flex justify-end">
              <button
                type="submit"
                :disabled="isSavingPassword"
                class="px-6 py-2 bg-[#D4956A] hover:bg-[#E8B48A] text-[#0A0A0B] text-sm rounded-2xl transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="isSavingPassword">{{ $t('settings.saving') }}</span>
                <span v-else>{{ $t('settings.change_password_button') }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <div v-show="activeTab === 'preferences'">
        <div class="bg-[#111113] rounded-2xl border border-[#2A2018] p-6 w-full">
          <h3 class="text-base font-semibold text-[#F5F0EA] mb-6">
            {{ $t('settings.preferences_section') }}
          </h3>
          <form @submit.prevent="handleSubmit('preferences')" class="space-y-6">
            <!-- Offensive limit -->
            <div>
              <label class="block text-sm font-medium text-[#9E9080] mb-1" for="offensive-limit">
                {{ $t('settings.offensive_limit_label') }}
              </label>
              <input
                id="offensive-limit"
                v-model="formData.offensive_limit"
                type="number"
                min="1"
                step="1"
                class="w-full px-4 py-2 border rounded-xl text-sm bg-[#1E1E23] text-[#F5F0EA] placeholder:text-[#9E9080] focus:outline-none focus:ring-2 focus:ring-[#D4956A]"
                :class="formErrors.offensive_limit ? 'border-red-400' : 'border-[#2A2018]'"
                :placeholder="$t('settings.offensive_limit_placeholder')"
              />
              <p v-if="formErrors.offensive_limit" class="mt-1 text-xs text-red-500">
                {{ formErrors.offensive_limit }}
              </p>
              <p v-else class="mt-1 text-xs text-[#524840]">
                {{ $t('settings.offensive_limit_hint') }}
              </p>
            </div>

            <!-- Reminder hours -->
            <div>
              <label class="block text-sm font-medium text-[#9E9080] mb-1" for="reminder-hours">
                {{ $t('settings.reminder_hours_label') }}
              </label>
              <input
                id="reminder-hours"
                v-model="formData.reminder_hours"
                type="number"
                min="1"
                :max="MAX_REMINDER_HOURS"
                step="1"
                class="w-full px-4 py-2 border rounded-xl text-sm bg-[#1E1E23] text-[#F5F0EA] placeholder:text-[#9E9080] focus:outline-none focus:ring-2 focus:ring-[#D4956A]"
                :class="formErrors.reminder_hours ? 'border-red-400' : 'border-[#2A2018]'"
                :placeholder="String(DEFAULT_REMINDER_HOURS)"
              />
              <p v-if="formErrors.reminder_hours" class="mt-1 text-xs text-red-500">
                {{ formErrors.reminder_hours }}
              </p>
              <p v-else class="mt-1 text-xs text-[#524840]">
                {{ $t('settings.reminder_hours_hint', { hours: DEFAULT_REMINDER_HOURS }) }}
              </p>
            </div>

            <!-- Max visits per day -->
            <div>
              <label class="block text-sm font-medium text-[#9E9080] mb-1" for="max-visits">
                {{ $t('settings.max_visits_per_day_label') }}
              </label>
              <input
                id="max-visits"
                v-model="formData.max_visits_per_day"
                type="number"
                min="1"
                :max="MAX_VISITS_PER_DAY"
                step="1"
                class="w-full px-4 py-2 border rounded-xl text-sm bg-[#1E1E23] text-[#F5F0EA] placeholder:text-[#9E9080] focus:outline-none focus:ring-2 focus:ring-[#D4956A]"
                :class="formErrors.max_visits_per_day ? 'border-red-400' : 'border-[#2A2018]'"
                :placeholder="String(DEFAULT_MAX_VISITS_PER_DAY)"
              />
              <p v-if="formErrors.max_visits_per_day" class="mt-1 text-xs text-red-500">
                {{ formErrors.max_visits_per_day }}
              </p>
              <p v-else class="mt-1 text-xs text-[#524840]">
                {{ $t('settings.max_visits_per_day_hint', { visits: DEFAULT_MAX_VISITS_PER_DAY }) }}
              </p>
            </div>
            <!-- Slot step -->
            <div>
              <label class="block text-sm font-medium text-[#9E9080] mb-1" for="slot-step">
                {{ $t('settings.slot_step_label') }}
              </label>
              <select
                id="slot-step"
                v-model.number="slotStepMinutes"
                class="w-full px-4 py-2 border border-[#2A2018] bg-[#1E1E23] text-[#F5F0EA] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4956A]"
              >
                <option v-for="step in SLOT_STEP_OPTIONS" :key="step" :value="step">
                  {{ $t('settings.slot_step_option', { minutes: step }) }}
                </option>
              </select>
              <p class="mt-1 text-xs text-[#524840]">{{ $t('settings.slot_step_hint') }}</p>
            </div>

            <!-- Messages -->
            <div
              v-if="feedback.preferences.success"
              class="px-4 py-3 bg-green-900/20 border border-green-800 rounded-xl text-sm text-green-400"
            >
              {{ feedback.preferences.success }}
            </div>
            <div
              v-if="feedback.preferences.error"
              class="px-4 py-3 bg-red-900/20 border border-red-800 rounded-xl text-sm text-red-400"
            >
              {{ feedback.preferences.error }}
            </div>

            <!-- Submit -->
            <div class="flex justify-end">
              <button
                type="submit"
                :disabled="feedback.preferences.saving"
                class="px-6 py-2 bg-[#D4956A] hover:bg-[#E8B48A] text-[#0A0A0B] text-sm rounded-2xl transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="feedback.preferences.saving">{{ $t('settings.saving') }}</span>
                <span v-else>{{ $t('settings.save_button') }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <div v-show="activeTab === 'agent'">
        <div class="bg-[#111113] rounded-2xl border border-[#2A2018] p-6 w-full">
          <h3 class="text-base font-semibold text-[#F5F0EA] mb-6">
            {{ $t('settings.agent_section') }}
          </h3>
          <form @submit.prevent="handleSubmit('agent')" class="space-y-6">
            <!-- Sex -->
            <div>
              <label class="block text-sm font-medium text-[#9E9080] mb-1" for="agent-sex">
                {{ $t('settings.sex_label') }}
              </label>
              <select
                id="agent-sex"
                v-model="formData.sex"
                class="w-full px-4 py-2 border border-[#2A2018] bg-[#1E1E23] text-[#F5F0EA] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4956A]"
              >
                <option v-for="option in SEX_OPTIONS" :key="option" :value="option">
                  {{ $t(`settings.sex_${option}`) }}
                </option>
              </select>
              <p class="mt-1 text-xs text-[#524840]">{{ $t('settings.sex_hint') }}</p>
            </div>

            <!-- Personality -->
            <div>
              <label class="block text-sm font-medium text-[#9E9080] mb-1">
                {{ $t('settings.personality_label') }}
              </label>
              <textarea
                v-model="formData.personality"
                rows="5"
                class="w-full px-4 py-2 border border-[#2A2018] bg-[#1E1E23] text-[#F5F0EA] placeholder:text-[#9E9080] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4956A] resize-none"
                :placeholder="$t('settings.personality_placeholder')"
              />
            </div>

            <!-- Tone style -->
            <div>
              <label class="block text-sm font-medium text-[#9E9080] mb-1">
                {{ $t('settings.tone_label') }}
              </label>
              <textarea
                v-model="formData.tone_style"
                rows="5"
                class="w-full px-4 py-2 border border-[#2A2018] bg-[#1E1E23] text-[#F5F0EA] placeholder:text-[#9E9080] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4956A] resize-none"
                :placeholder="$t('settings.tone_placeholder')"
              />
            </div>

            <!-- Messages -->
            <div
              v-if="feedback.agent.success"
              class="px-4 py-3 bg-green-900/20 border border-green-800 rounded-xl text-sm text-green-400"
            >
              {{ feedback.agent.success }}
            </div>
            <div
              v-if="feedback.agent.error"
              class="px-4 py-3 bg-red-900/20 border border-red-800 rounded-xl text-sm text-red-400"
            >
              {{ feedback.agent.error }}
            </div>

            <!-- Submit -->
            <div class="flex justify-end">
              <button
                type="submit"
                :disabled="feedback.agent.saving"
                class="px-6 py-2 bg-[#D4956A] hover:bg-[#E8B48A] text-[#0A0A0B] text-sm rounded-2xl transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="feedback.agent.saving">{{ $t('settings.saving') }}</span>
                <span v-else>{{ $t('settings.save_button') }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <div v-show="activeTab === 'twofa'">
        <div class="bg-[#111113] rounded-2xl border border-[#2A2018] p-6 w-full">
          <div class="flex items-center justify-between mb-1">
            <h3 class="text-base font-semibold text-[#F5F0EA]">
              {{ $t('settings.twofa_section') }}
            </h3>
            <span
              v-if="user?.twofa_enabled"
              class="px-2.5 py-0.5 text-xs font-medium rounded-full bg-green-900/30 text-green-400"
            >
              {{ $t('settings.twofa_enabled_badge') }}
            </span>
            <span
              v-else
              class="px-2.5 py-0.5 text-xs font-medium rounded-full bg-[#1E1E23] text-[#524840]"
            >
              {{ $t('settings.twofa_disabled_badge') }}
            </span>
          </div>

          <p class="text-sm text-[#524840] mb-5">
            {{
              user?.twofa_enabled
                ? $t('settings.twofa_enabled_desc')
                : $t('settings.twofa_disabled_desc')
            }}
          </p>

          <!-- Success message -->
          <div
            v-if="twoFaSuccessMessage"
            class="mb-4 px-4 py-3 bg-green-900/20 border border-green-800 rounded-xl text-sm text-green-400"
          >
            {{ twoFaSuccessMessage }}
          </div>

          <!-- Error message (load error) -->
          <div
            v-if="twoFaErrorMessage"
            class="mb-4 px-4 py-3 bg-red-900/20 border border-red-800 rounded-xl text-sm text-red-400"
          >
            {{ twoFaErrorMessage }}
          </div>

          <!-- Idle state -->
          <div v-if="twoFaMode === 'idle'">
            <button
              v-if="!user?.twofa_enabled"
              @click="startEnable2fa"
              class="px-5 py-2 bg-[#D4956A] hover:bg-[#E8B48A] text-[#0A0A0B] text-sm rounded-2xl transition"
            >
              {{ $t('settings.twofa_enable_button') }}
            </button>
            <button
              v-else
              @click="startDisable2fa"
              class="px-5 py-2 bg-red-600 hover:bg-red-700 text-[#F5F0EA] text-sm rounded-2xl transition"
            >
              {{ $t('settings.twofa_disable_button') }}
            </button>
          </div>

          <!-- Enable flow: QR code + code input -->
          <div v-else-if="twoFaMode === 'enabling'" class="space-y-5">
            <div>
              <p class="text-sm font-medium text-[#9E9080] mb-3">
                {{ $t('settings.twofa_scan_title') }}
              </p>
              <p class="text-sm text-[#524840] mb-4">
                {{ $t('settings.twofa_scan_desc') }}
              </p>
              <div
                v-if="twoFaQrDataUrl"
                class="inline-block p-3 bg-[#111113] rounded-xl border border-[#2A2018]"
              >
                <img :src="twoFaQrDataUrl" alt="QR Code" class="w-48 h-48" />
              </div>
              <div v-else class="w-48 h-48 bg-[#1E1E23] rounded-xl animate-pulse" />
            </div>

            <div>
              <label class="block text-sm font-medium text-[#9E9080] mb-3">
                {{ $t('settings.twofa_code_label') }}
              </label>
              <div class="flex gap-3">
                <input
                  v-for="(_, index) in twoFaDigits"
                  :key="index"
                  :ref="
                    (el) => {
                      if (el) twoFaInputs[index] = el as HTMLInputElement;
                    }
                  "
                  type="text"
                  inputmode="numeric"
                  maxlength="1"
                  :value="twoFaDigits[index]"
                  :disabled="isTwoFaProcessing"
                  @input="handleTwoFaDigit(index, $event)"
                  @keydown="handleTwoFaKeydown(index, $event)"
                  @paste="handleTwoFaPaste"
                  class="w-12 h-14 text-center text-xl text-[#F5F0EA] bg-[#18181C] border rounded-2xl focus:bg-[#111113] focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition disabled:opacity-50 disabled:cursor-not-allowed"
                  :class="twoFaCodeError ? 'border-red-400' : 'border-[#2A2018]'"
                />
              </div>
              <p v-if="twoFaCodeError" class="mt-2 text-xs text-red-500">{{ twoFaCodeError }}</p>
            </div>

            <div class="flex gap-3">
              <button
                @click="confirmEnable2fa"
                :disabled="isTwoFaProcessing"
                class="px-5 py-2 bg-[#D4956A] hover:bg-[#E8B48A] text-[#0A0A0B] text-sm rounded-2xl transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{
                  isTwoFaProcessing
                    ? $t('settings.twofa_processing')
                    : $t('settings.twofa_confirm_enable')
                }}
              </button>
              <button
                @click="cancelTwoFa"
                :disabled="isTwoFaProcessing"
                class="px-5 py-2 border border-[#2A2018] text-[#9E9080] text-sm rounded-2xl hover:bg-[#1E1E23] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ $t('settings.twofa_cancel') }}
              </button>
            </div>
          </div>

          <!-- Disable flow: code input -->
          <div v-else-if="twoFaMode === 'disabling'" class="space-y-5">
            <div>
              <p class="text-sm font-medium text-[#9E9080] mb-1">
                {{ $t('settings.twofa_disable_title') }}
              </p>
              <p class="text-sm text-[#524840]">
                {{ $t('settings.twofa_disable_desc') }}
              </p>
            </div>

            <div>
              <label class="block text-sm font-medium text-[#9E9080] mb-3">
                {{ $t('settings.twofa_code_label') }}
              </label>
              <div class="flex gap-3">
                <input
                  v-for="(_, index) in twoFaDigits"
                  :key="index"
                  :ref="
                    (el) => {
                      if (el) twoFaInputs[index] = el as HTMLInputElement;
                    }
                  "
                  type="text"
                  inputmode="numeric"
                  maxlength="1"
                  :value="twoFaDigits[index]"
                  :disabled="isTwoFaProcessing"
                  @input="handleTwoFaDigit(index, $event)"
                  @keydown="handleTwoFaKeydown(index, $event)"
                  @paste="handleTwoFaPaste"
                  class="w-12 h-14 text-center text-xl text-[#F5F0EA] bg-[#18181C] border rounded-2xl focus:bg-[#111113] focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition disabled:opacity-50 disabled:cursor-not-allowed"
                  :class="twoFaCodeError ? 'border-red-400' : 'border-[#2A2018]'"
                />
              </div>
              <p v-if="twoFaCodeError" class="mt-2 text-xs text-red-500">{{ twoFaCodeError }}</p>
            </div>

            <div class="flex gap-3">
              <button
                @click="confirmDisable2fa"
                :disabled="isTwoFaProcessing"
                class="px-5 py-2 bg-red-600 hover:bg-red-700 text-[#F5F0EA] text-sm rounded-2xl transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{
                  isTwoFaProcessing
                    ? $t('settings.twofa_processing')
                    : $t('settings.twofa_confirm_disable')
                }}
              </button>
              <button
                @click="cancelTwoFa"
                :disabled="isTwoFaProcessing"
                class="px-5 py-2 border border-[#2A2018] text-[#9E9080] text-sm rounded-2xl hover:bg-[#1E1E23] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ $t('settings.twofa_cancel') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-show="activeTab === 'schedule'">
        <WeeklyScheduleCard />
      </div>
    </div>
  </div>
</template>
